import { useCallback, useContext, useState } from 'react';

import moment from 'moment/moment';
import { useDispatch, useSelector } from 'react-redux';

import { useProgressMediaData } from './useProgressMediaData';
import { useUploadPreview } from './useUploadPreview';
import { getProgressMediaList } from '../../../../../store/slices/dashboardSlice/action';
import {
    dashboardActions,
    progressSelector,
} from '../../../../../store/slices/dashboardSlice/dashboardSlice';
import { ProgressContext } from '../../../Context/ProgressContext';
import { useUploadProgressMediaItem } from '../../../hooks/useUploadProgressMediaItem';

export const useConstructionProgressDnD = () => {
    const dispatch = useDispatch();
    const { uploadMediaItem } = useUploadProgressMediaItem();

    const [prevMediaData, setPrevMediaData] = useState({
        currentIndex: 0,
        currentUrl: '',
        mimeType: '',
        date: '',
        description: '',
    });

    const progress = useSelector(progressSelector.selectAll);
    const {
        onImageChange,
        uploadMediaList,
        uploadedProgressMediaList,
        isUploadQueueStarted,
        abortUploadMediaItem,
        clearUploadedProgressMediaList,
    } = useContext(ProgressContext);

    const { mediaData, uploadedTempMediaData, previewMediaMeta, previewMediaData } =
        useProgressMediaData(progress, uploadedProgressMediaList);

    const setCurrentIndex = useCallback(
        (index: number) => {
            setPrevMediaData((prevState) => ({
                ...prevState,
                currentIndex: index,
                date: previewMediaMeta[index]?.date || '',
                description: previewMediaMeta[index]?.description || '',
            }));
        },
        [previewMediaMeta]
    );
    const setCurrentUrl = useCallback(
        (url: string) => {
            setPrevMediaData((prevState) => ({ ...prevState, currentUrl: url }));
        },
        [prevMediaData]
    );
    const setCurrentMimeType = useCallback(
        (mimeType: string) => {
            setPrevMediaData((prevState) => ({ ...prevState, mimeType }));
        },
        [prevMediaData]
    );
    const onHideModal = useCallback(() => {
        setPrevMediaData({
            currentIndex: 0,
            currentUrl: '',
            mimeType: '',
            date: '',
            description: '',
        });
    }, []);
    const onShowPrevMedia = useCallback(
        (
            currentIndex: number,
            currentUrl: string,
            mimeType: string,
            date = '',
            description = ''
        ) => {
            setPrevMediaData({
                currentIndex,
                currentUrl,
                mimeType,
                date,
                description,
            });
        },
        []
    );

    const mediaIds = useSelector(progressSelector.selectIds);

    // @ts-ignore
    const { is_customer, is_owner, progress_tariff_access_edit } = useSelector(
        // @ts-ignore
        (state) => state.dashboardReducer
    );

    const progressAll = useSelector(progressSelector.selectAll);
    const { uploadingMediaData } = useUploadPreview(uploadMediaList);

    const onEdit = useCallback(() => {
        const { id } = progressAll.find((image) => image.original_url === prevMediaData.currentUrl);
        dispatch(dashboardActions.changeCurrentProgressId(id));
    }, []);

    const onRetryUpload = useCallback(
        async (id: string) => {
            try {
                await uploadMediaItem(id, (mediaItem) => ({
                    date: mediaItem.date || moment().format(),
                    description: mediaItem.description || '',
                }));
                await dispatch(getProgressMediaList()).unwrap();
                clearUploadedProgressMediaList();
            } catch {
                // Error state is already written into upload item by useUploadProgressMediaItem.
            }
        },
        [clearUploadedProgressMediaList, dispatch, uploadMediaItem]
    );

    return {
        onImageChange,
        mediaIds,
        is_customer,
        is_owner,
        progress_tariff_access_edit,
        mediaData,
        setCurrentIndex,
        setCurrentUrl,
        setCurrentMimeType,
        onHideModal,
        onShowPrevMedia,
        prevMediaData,
        progress,
        onEdit,
        uploadingMediaData,
        uploadedTempMediaData,
        previewMediaData,
        previewMediaMeta,
        isUploadQueueStarted,
        abortUploadMediaItem,
        onRetryUpload,
    };
};
